import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Icon,
  Menu,
  Pagination,
  Paragraph,
  Popover,
  Table,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconArrowDownFilled, IconArrowUpFilled } from '@zillow/constellation-icons';
import { css } from '@/styled-system/css';
import { styled } from '@/styled-system/jsx';
import type {
  CustomerAnalyticsEnterpriseAnalyticsDto,
  CustomerAnalyticsListingPerformanceDtoV2,
} from '@zg-rentals/ts-customer-analytics-client';
import { debounce } from '@zg-rentals/zdash';
import { formatNumber } from '../../../../../utils/formatNumber';
import { adproductsUtils } from '@zg-rentals/rental-revenue-components';
import { formatSpotlightDates } from '../../../../../utils/formatSpotlightDates';
import { MarketTrendsDownloadCell } from '../../../AdSpend/Report/MarketTrendsDownloadCell';

type SortColumn =
  | 'property'
  | 'productTypes'
  | 'publishedStatus'
  | 'impressionCount'
  | 'viewCount'
  | 'listingWebReferrals'
  | 'leadCount'
  | 'phoneLeadCount'
  | 'emailLeadCount'
  | 'boostImpressions'
  | 'boostClickCount'
  | 'daysOnMarketCount'
  | 'totalDaysSpotlighted';

type SortDirection = 'asc' | 'desc';

interface FunnelBreakdownTableProps {
  data: CustomerAnalyticsListingPerformanceDtoV2 | undefined;
  onRowClick: (listingAlias: string, propertyName: string) => void;
  isLoading: boolean;
}

const stickyStyles = css({
  position: 'sticky',
  left: 0,
  zIndex: 2,
  backgroundColor: 'white',
  minWidth: '200px',
  maxWidth: '300px',
  _before: {
    content: '""',
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '15px',
    pointerEvents: 'none',
    boxShadow: '5px 0 5px rgba(0, 0, 0, 0.15)',
  },
  '&.is-stuck': {
    _before: {
      display: 'none',
    },
  },
});

const StyledRow = styled(Table.Row, {
  base: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    _hover: {
      '& td': {
        backgroundColor: 'gray.50',
      },
    },
  },
});

const SortableHeader = styled(Table.HeaderCell, {
  base: {
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
    _hover: {
      backgroundColor: 'gray.50',
    },
  },
});

const PAGE_SIZES = [50, 100, 200];

const MarketTrendsCell = ({ listing }: { listing: CustomerAnalyticsEnterpriseAnalyticsDto }) => {
  if (listing.isSingleFamily || !listing.adproductTypes?.length) {
    return <Text textStyle="body-sm">-</Text>;
  }
  if (!listing.hasMoodysData) {
    return (
      <Text textStyle="body-sm" css={{ whiteSpace: 'nowrap' }}>
        Coming soon
      </Text>
    );
  }
  return <MarketTrendsDownloadCell listingAlias={listing.listingAlias} />;
};

export const FunnelBreakdownTable = ({ data, onRowClick, isLoading }: FunnelBreakdownTableProps) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [sortColumn, setSortColumn] = useState<SortColumn>('property');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const tableRef = useRef<HTMLDivElement>(null);
  const [isInTheLeft, setIsInTheLeft] = useState(true);

  const allListings = useMemo(() => data?.analytics ?? [], [data?.analytics]);

  const sortedListings = useMemo(() => {
    const sorted = [...allListings].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortColumn === 'property') {
        aValue = ((a.isSingleFamily ? a.street : a.listingName || a.street) || '').toLowerCase();
        bValue = ((b.isSingleFamily ? b.street : b.listingName || b.street) || '').toLowerCase();
      } else if (sortColumn === 'productTypes') {
        aValue = adproductsUtils
          .mapProductTypes(a.adproductTypes || [], a.analyticsListingType)
          .join(' ')
          .toLowerCase();
        bValue = adproductsUtils
          .mapProductTypes(b.adproductTypes || [], b.analyticsListingType)
          .join(' ')
          .toLowerCase();
      } else if (sortColumn === 'publishedStatus') {
        aValue = (a.publishedStatus || '').toLowerCase();
        bValue = (b.publishedStatus || '').toLowerCase();
      } else {
        aValue = a[sortColumn] ?? 0;
        bValue = b[sortColumn] ?? 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [allListings, sortColumn, sortDirection]);

  const paginatedListings = useMemo(() => {
    const startIndex = currentPageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedListings.slice(startIndex, endIndex);
  }, [sortedListings, currentPageIndex, pageSize]);

  const totalPages = Math.ceil(sortedListings.length / pageSize);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return null;
    return (
      <Icon size="sm" css={{ marginLeft: '100', verticalAlign: 'middle' }}>
        {sortDirection === 'asc' ? <IconArrowUpFilled /> : <IconArrowDownFilled />}
      </Icon>
    );
  };

  // Check if there are any single family listings
  const hasSingleFamilyListings = useMemo(() => {
    return allListings.some((listing) => listing.isSingleFamily);
  }, [allListings]);

  // Check if there are any listings with spotlight days
  const hasSpotlightDays = useMemo(() => {
    return allListings.some((listing) => (listing.totalDaysSpotlighted ?? 0) > 0);
  }, [allListings]);

  // Reset to page 0 when data changes or sort changes
  useEffect(() => {
    setCurrentPageIndex(0);
  }, [sortedListings.length, pageSize, sortColumn, sortDirection]);

  const onScrollWrapper = useMemo(
    () =>
      debounce(() => {
        const container = tableRef.current;
        if (!container) return;

        const isScrollInTheLeft = container.scrollLeft <= 1;
        setIsInTheLeft(isScrollInTheLeft);
      }, 50),
    [],
  );

  useEffect(() => {
    const container = tableRef.current;
    if (!container) return;

    container.addEventListener('scroll', onScrollWrapper);
    window.addEventListener('resize', onScrollWrapper);
    onScrollWrapper();

    return () => {
      container.removeEventListener('scroll', onScrollWrapper);
      window.removeEventListener('resize', onScrollWrapper);
    };
  }, [onScrollWrapper]);

  if (isLoading) {
    return (
      <Text textStyle="body-sm" css={{ textAlign: 'center', padding: '800' }}>
        Loading...
      </Text>
    );
  }

  if (!data || data.analytics.length === 0) {
    return (
      <Text textStyle="body-sm" css={{ textAlign: 'center', padding: '800' }}>
        No properties found for the selected filters and time range.
      </Text>
    );
  }

  return (
    <>
      <Table.Responsive ref={tableRef}>
        <Table.Root aria-label="Listing performance breakdown table">
          <Table.Header>
            <Table.Row>
              <SortableHeader
                className={`${stickyStyles} ${isInTheLeft ? 'is-stuck' : ''}`}
                onClick={() => handleSort('property')}
              >
                Property{renderSortIcon('property')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'left' }} onClick={() => handleSort('productTypes')}>
                Advertising type{renderSortIcon('productTypes')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'left' }} onClick={() => handleSort('publishedStatus')}>
                Published status{renderSortIcon('publishedStatus')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('impressionCount')}>
                Impressions{renderSortIcon('impressionCount')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('viewCount')}>
                Listing views{renderSortIcon('viewCount')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('listingWebReferrals')}>
                Listing web referrals{renderSortIcon('listingWebReferrals')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('leadCount')}>
                Total leads{renderSortIcon('leadCount')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('phoneLeadCount')}>
                Phone leads{renderSortIcon('phoneLeadCount')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('emailLeadCount')}>
                Email leads{renderSortIcon('emailLeadCount')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('boostImpressions')}>
                Ad impressions{renderSortIcon('boostImpressions')}
              </SortableHeader>
              <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('boostClickCount')}>
                Ad clicks{renderSortIcon('boostClickCount')}
              </SortableHeader>
              {hasSingleFamilyListings && (
                <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('daysOnMarketCount')}>
                  Days on market{renderSortIcon('daysOnMarketCount')}
                </SortableHeader>
              )}
              {hasSpotlightDays && (
                <SortableHeader css={{ textAlign: 'right' }} onClick={() => handleSort('totalDaysSpotlighted')}>
                  Spotlight days{renderSortIcon('totalDaysSpotlighted')}
                </SortableHeader>
              )}
              <Table.HeaderCell css={{ whiteSpace: 'nowrap' }}>Market Trends</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedListings.map((listing) => {
              const propertyName = listing.isSingleFamily ? listing.street : listing.listingName || listing.street;
              const unitName = listing.isSingleFamily ? (listing.unit || listing.unitName || '').toUpperCase() : '';

              return (
                <StyledRow key={listing.listingAlias} onClick={() => onRowClick(listing.listingAlias, propertyName)}>
                  <Table.Cell className={`${stickyStyles} ${isInTheLeft ? 'is-stuck' : ''}`}>
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
                      <TextButton
                        css={{
                          textAlign: 'left',
                          display: 'block',
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <Paragraph
                          css={{
                            whiteSpace: 'nowrap',
                            textDecoration: 'underline',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {propertyName}
                          {unitName && `: ${unitName}`}
                        </Paragraph>
                        {listing.city && listing.state && (
                          <Paragraph css={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {listing.city} {listing.state}
                          </Paragraph>
                        )}
                      </TextButton>
                      {listing.totalDaysSpotlighted > 0 && listing.localDatesSpotlighted && (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <Popover
                            tone="neutral"
                            header={<Text textStyle="body-sm-bold">Listing Spotlight</Text>}
                            body={
                              <Box css={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {formatSpotlightDates(listing.localDatesSpotlighted).map((item, index) => (
                                  <Text key={index} textStyle="body-sm">
                                    {`${item.range} (${item.count} day${item.count > 1 ? 's' : ''})`}
                                  </Text>
                                ))}
                              </Box>
                            }
                            trigger={
                              <Tag
                                css={{
                                  textTransform: 'capitalize',
                                  cursor: 'pointer',
                                }}
                                size="md"
                                tone="blue"
                              >
                                Spotlight
                              </Tag>
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'left' }}>
                    <Text textStyle="body-sm">
                      {adproductsUtils
                        .mapProductTypes(listing.adproductTypes || [], listing.analyticsListingType)
                        .join(', ')
                        .replace('™', '') || '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'left' }}>
                    <Text textStyle="body-sm">{listing.publishedStatus || '-'}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.impressionCount)}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.viewCount)}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.listingWebReferrals)}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.leadCount)}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.phoneLeadCount)}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.emailLeadCount)}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.boostImpressions)}</Text>
                  </Table.Cell>
                  <Table.Cell css={{ textAlign: 'right' }}>
                    <Text textStyle="body-sm">{formatNumber(listing.boostClickCount)}</Text>
                  </Table.Cell>
                  {hasSingleFamilyListings && (
                    <Table.Cell css={{ textAlign: 'right' }}>
                      <Text textStyle="body-sm">{formatNumber(listing.daysOnMarketCount)}</Text>
                    </Table.Cell>
                  )}
                  {hasSpotlightDays && (
                    <Table.Cell css={{ textAlign: 'right' }}>
                      <Text textStyle="body-sm">{formatNumber(listing.totalDaysSpotlighted)}</Text>
                    </Table.Cell>
                  )}
                  <Table.Cell onClick={(e) => e.stopPropagation()}>
                    <MarketTrendsCell listing={listing} />
                  </Table.Cell>
                </StyledRow>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Table.Responsive>
      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 'md',
          flexWrap: 'wrap',
          gap: 'md',
        }}
      >
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'sm' }}>
          <Paragraph css={{ textStyle: 'body-sm' }}>Rows per page:</Paragraph>
          <Menu
            content={PAGE_SIZES.map((size) => (
              <Menu.Item key={size} onClick={() => setPageSize(size)}>
                {size}
              </Menu.Item>
            ))}
            trigger={<Menu.Button size="sm">{pageSize}</Menu.Button>}
          />
        </Box>
        {totalPages > 1 && (
          <Pagination
            currentPageIndex={currentPageIndex}
            totalPages={totalPages}
            onPageSelected={setCurrentPageIndex}
          />
        )}
      </Box>
    </>
  );
};
